import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { deepOrange } from '@mui/material/colors';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { State } from '../../models/state';
import { logout } from '../../store/actions/auth';
import './Layout.scss';

interface HeaderProps {
  menuItems: {
    name: string;
    icon: React.ReactNode;
    path: string;
    isAdmin?: boolean;
  }[];
}

export default function Layout({ menuItems }: HeaderProps) {
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useSelector((state: State) => state.auth);
  const [openProfileMenu, setOpenProfileMenu] = useState(false);
  const [openMenu, setOpenMenu] = useState(true);
  const anchorRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (location?.pathname?.includes('/survey-series/') && location?.pathname?.includes('/survey/')) {
      setOpenMenu(false);
    }
  }, [location.pathname]);

  const avatarStyle = {
    bgcolor: deepOrange[400],
    textTransform: 'uppercase',
    cursor: 'pointer',
  };
  const drawerWidth = 200;

  const handlePath = (path: string) => {
    navigate(path);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpenProfileMenu(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpenProfileMenu(false);
    } else if (event.key === 'Escape') {
      setOpenProfileMenu(false);
    }
  }

  const handleLogout = () => {
    dispatch(logout());
  };

  const profileMenu = (
    <Box>
      <Button
        ref={anchorRef}
        id='composition-button'
        aria-controls={openMenu ? 'composition-menu' : undefined}
        aria-expanded={openMenu ? 'true' : undefined}
        aria-haspopup='true'
        onClick={() => setOpenProfileMenu(prev => !prev)}
      >
        <Avatar sx={avatarStyle}>{user?.email?.[0]}</Avatar>
      </Button>
      <Popper
        open={openProfileMenu}
        anchorEl={anchorRef.current}
        role={undefined}
        placement='bottom-start'
        transition
        disablePortal
        sx={{ width: 150, pr: 3 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={openProfileMenu}
                  id='composition-menu'
                  aria-labelledby='composition-button'
                  onKeyDown={handleListKeyDown}
                >
                  <MenuItem
                    onClick={e => {
                      handlePath('/profile');
                      handleClose(e);
                    }}
                  >
                    Profile
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );

  return (
    <>
      <Box sx={{ display: 'flex' }} className='layout'>
        <AppBar position='fixed' sx={{ zIndex: 5 }}>
          <Toolbar className='header'>
            <IconButton size='large' edge='start' color='inherit' aria-label='menu' onClick={() => setOpenMenu(true)}>
              <MenuIcon />
            </IconButton>
            <Box className='initials'>{profileMenu}</Box>
          </Toolbar>
        </AppBar>
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
          variant='persistent'
          anchor='left'
          open={openMenu}
        >
          <IconButton onClick={() => setOpenMenu(false)} className='menu-header'>
            <Typography variant='h5'>Menu</Typography>
            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
          <Divider />
          <List>
            {menuItems
              .filter(el => (!user.isAdmin ? !el.isAdmin : true))
              .map(menuItem => (
                <ListItem key={menuItem.name} disablePadding onClick={() => handlePath(menuItem.path)}>
                  <ListItemButton
                    selected={
                      menuItem.path.slice(1)
                        ? window?.location?.pathname?.slice(1).startsWith(menuItem.path.slice(1))
                        : window?.location?.pathname === '/'
                    }
                  >
                    <ListItemIcon>{menuItem.icon}</ListItemIcon>
                    <ListItemText primary={menuItem.name} />
                  </ListItemButton>
                </ListItem>
              ))}
          </List>
        </Drawer>
      </Box>
      <Box
        sx={{
          ml: openMenu ? drawerWidth / 8 : 0,
          p: 4,
          pt: 11,
        }}
      >
        <Outlet />
      </Box>
    </>
  );
}
